import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';

@Component({
  selector: 'app-audio-recorder',
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['./audio-recorder.component.scss'],
  standalone: true,
  imports: [
    CommonModule
  ]
})

export class AudioRecorderComponent implements OnInit, OnDestroy {

  @ViewChild('audioPreview') audioPreview!: ElementRef<HTMLAudioElement>;

  @Input() maxRecordingTime: number = 60;

  @Output() recordingStarted = new EventEmitter<void>();
  @Output() recordingStopped = new EventEmitter<void>();
  @Output() recordingComplete = new EventEmitter<{ blob: Blob, url: string }>;


  isRecording = false;
  audioURL: string | null = null;
  remainingTime = 60; // 60 seconds default recording limit
  audioDevices: MediaDeviceInfo[] = [];
  currentDeviceId: string = '';
  timerInterval: any;
  audioRecorderReady: boolean = false;
  recordedAudioUrl: string | null = null;

  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private timer: any;
  private stream: MediaStream | null = null;

  ngOnInit(): void {
    this.getAudioDevices();
  }

  ngOnDestroy(): void {
    this.stopMediaTracks();
    this.clearTimer();
  }

  async getAudioDevices(): Promise<void> {
    try {
      // Request permission to access audio
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Get all available media devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.audioDevices = devices.filter(device => device.kind === 'audioinput');

      if (this.audioDevices.length > 0) {
        this.currentDeviceId = this.audioDevices[0].deviceId;
      }
    } catch (error) {
      console.error("Error accessing media devices:", error);
    }
  }

  async startRecording(): Promise<void> {
    try {
      this.stopMediaTracks();

      // Get media stream with selected device
      const streamOptions = {
        audio: this.currentDeviceId ? { deviceId: { exact: this.currentDeviceId } } : true
      };

      this.stream = await navigator.mediaDevices.getUserMedia(streamOptions);
      this.mediaRecorder = new MediaRecorder(this.stream);

      // Reset recording state
      this.audioChunks = [];
      this.audioURL = null;
      this.remainingTime = 60;

      // Start recording
      this.mediaRecorder.start();
      this.isRecording = true;

      // Emit recording started event
      this.recordingStarted.emit();

      // Set up timer
      this.timer = setInterval(() => {
        this.remainingTime--;
        if (this.remainingTime <= 0) {
          this.stopRecording();
        }
      }, 1000);

      // Collect audio chunks
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      // When recording stops
      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
        this.audioURL = URL.createObjectURL(audioBlob);

        // Emit recording complete event with blob and URL
        this.recordingComplete.emit({
          blob: audioBlob,
          url: this.audioURL
        });
      };
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.stopMediaTracks();
      this.clearTimer();
      this.isRecording = false;

      // Emit recording stopped event
      this.recordingStopped.emit();
    }
  }

  toggleRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  switchMicrophone(): void {
    if (this.isRecording) {
      this.stopRecording();
    }

    if (this.audioDevices.length > 1) {
      const currentIndex = this.audioDevices.findIndex(device => device.deviceId === this.currentDeviceId);
      const nextIndex = (currentIndex + 1) % this.audioDevices.length;
      this.currentDeviceId = this.audioDevices[nextIndex].deviceId;
    }
  }

  formatRemainingTime(): string {
    const minutes = Math.floor(this.remainingTime / 60);
    const seconds = this.remainingTime % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  private stopMediaTracks(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }

  private clearTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  getCurrentMicrophoneName(): string {
    const device = this.audioDevices.find(d => d.deviceId === this.currentDeviceId);
    return device?.label || 'Default microphone';
  }

  cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.mediaRecorder) {
      if (this.isRecording) {
        this.mediaRecorder.stop();
      }
      this.mediaRecorder = null;
    }

    clearInterval(this.timerInterval);
    this.audioRecorderReady = false;
    this.recordedAudioUrl = null;
    this.remainingTime = this.maxRecordingTime;
  }
}